import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, errText, mediaUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Avatar } from "../components/common";
import { Camera, Check, Star, ArrowRight, ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { WEB_DOMAIN } from "../lib/config";

const WORLDS = ["Second Life", "IMVU", "FiveM", "VRChat", "Roblox", "Other"];
const ROLES = ["Artist", "Influencer", "Actor", "Model", "DJ", "Producer", "Videographer", "Photographer", "Director", "Designer", "Streamer", "Dancer", "Journalist", "Promoter", "Venue", "Business", "Label", "TV Network", "Creator"];

export default function Onboarding() {
  const { user, patchUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    display_name: user?.display_name || "", username: user?.username || "", bio: "",
    avatar_url: user?.avatar_url || null, banner_url: null,
    worlds: [], primary: null, secondary: null, links: [{ world: "", url: "" }], plan: "free",
  });
  const avatarRef = useRef(); const bannerRef = useRef();
  const [upLoading, setUpLoading] = useState("");
  const [unameCheck, setUnameCheck] = useState(null);

  useEffect(() => {
    const clean = (form.username || "").replace(/^@/, "");
    if (!clean) { setUnameCheck(null); return; }
    if (clean.toLowerCase() === (user?.username || "").toLowerCase()) { setUnameCheck({ available: true, reason: "Your current username" }); return; }
    setUnameCheck({ loading: true });
    const t = setTimeout(() => {
      api.get(`/username/check?username=${encodeURIComponent(clean)}`).then(({ data }) => setUnameCheck(data)).catch(() => setUnameCheck(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.username]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleWorld = (w) => set("worlds", form.worlds.includes(w) ? form.worlds.filter((x) => x !== w) : [...form.worlds, w]);

  const upload = async (file, key) => {
    setUpLoading(key);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set(key, data.url); }
    catch (e) { toast.error(errText(e)); } finally { setUpLoading(""); }
  };

  const genBio = async () => {
    try { const { data } = await api.post("/ai/generate-bio", { keywords: `${form.primary || "creator"} in ${form.worlds.join(", ") || "virtual worlds"}, ${form.display_name}` }); set("bio", data.bio); }
    catch (e) { toast.error(errText(e)); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/profile", {
        display_name: form.display_name, username: form.username, bio: form.bio,
        avatar_url: form.avatar_url, banner_url: form.banner_url, worlds: form.worlds,
        roles: { primary: form.primary, secondary: form.secondary },
        external_links: form.links.filter((l) => l.url), plan: form.plan, onboarded: true,
      });
      const { data } = await api.get("/auth/me"); patchUser(data);
      setStep(6);
      setTimeout(() => nav("/home"), 2200);
    } catch (e) { toast.error(errText(e)); setBusy(false); }
  };

  const next = () => {
    if (step === 1 && (!form.display_name || !form.username)) return toast.error("Add a display name and @username");
    if (step === 1 && unameCheck && !unameCheck.loading && !unameCheck.available) return toast.error(unameCheck.reason || "Choose an available username");
    if (step === 2 && form.worlds.length === 0) return toast.error("Pick at least one world");
    if (step === 3 && !form.primary) return toast.error("Choose your primary role");
    if (step === 5) return finish();
    setStep(step + 1);
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white flex items-center justify-center p-5 relative overflow-hidden starfield">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(0,110,150,0.2),transparent_55%)]" />
      <div className="relative w-full max-w-xl">
        {step < 6 && (
          <>
            <div className="flex items-center gap-1.5 mb-6">{[1, 2, 3, 4, 5].map((s) => <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-eblue" : "bg-white/10"}`} />)}</div>
            <div className="glass rounded-2xl p-6 md:p-8 animate-rise" key={step}>
              {step === 1 && (<>
                <H title="Create Your VStar" sub="This is your identity across every virtual world." />
                <div className="relative h-28 rounded-xl surface-2 overflow-hidden mb-12 cursor-pointer" onClick={() => bannerRef.current?.click()}>
                  {form.banner_url ? <img src={mediaUrl(form.banner_url)} className="w-full h-full object-cover" alt="" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm gap-2"><Camera size={16} /> Add banner</div>}
                  {upLoading === "banner_url" && <div className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 className="animate-spin" /></div>}
                  <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "banner_url")} />
                  <div className="absolute -bottom-9 left-5 cursor-pointer" onClick={(e) => { e.stopPropagation(); avatarRef.current?.click(); }}>
                    <div className="relative"><Avatar src={form.avatar_url} name={form.display_name} size={72} className="ring-4 ring-[#030303]" /><span className="absolute bottom-0 right-0 w-6 h-6 rounded-full btn-blue flex items-center justify-center"><Camera size={13} /></span></div>
                    <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "avatar_url")} />
                  </div>
                </div>
                <div className="space-y-3">
                  <Input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="Display name" className="h-11 bg-transparent border-white/10" data-testid="onb-display-name" />
                  <div className="flex items-center surface-2 border border-white/10 rounded-md h-11 px-3"><span className="text-muted-foreground">@</span><input value={form.username} onChange={(e) => set("username", e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, ""))} placeholder="username" className="bg-transparent flex-1 outline-none text-sm" data-testid="onb-username" />{unameCheck?.loading && <Loader2 size={14} className="animate-spin text-muted-foreground" />}{unameCheck && !unameCheck.loading && unameCheck.available && <Check size={15} className="text-emerald-400" data-testid="onb-username-available" />}{unameCheck && !unameCheck.loading && !unameCheck.available && <span className="text-rose-400 text-xs">✕</span>}</div>
                  <div className="text-[11px] text-eblue -mt-1">This becomes your unique VirtuStars identity · {WEB_DOMAIN}/{form.username || "username"}</div>
                  {unameCheck && !unameCheck.loading && (<div className={`text-[11px] ${unameCheck.available ? "text-emerald-400" : "text-rose-400"}`} data-testid="onb-username-reason">{unameCheck.reason}</div>)}
                  {unameCheck && !unameCheck.loading && !unameCheck.available && unameCheck.suggestions?.length > 0 && (<div className="flex flex-wrap gap-1.5">{unameCheck.suggestions.map((s) => <button type="button" key={s} onClick={() => set("username", s)} className="text-[11px] surface-2 rounded-full px-2 py-0.5 text-eblue" data-testid={`onb-suggest-${s}`}>@{s}</button>)}</div>)}
                  <div className="relative"><Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Short bio" className="bg-transparent border-white/10 min-h-[70px]" data-testid="onb-bio" /><button onClick={genBio} type="button" className="absolute bottom-2 right-2 text-xs text-eblue flex items-center gap-1 glass rounded-full px-2 py-1"><Sparkles size={12} /> AI</button></div>
                </div>
              </>)}

              {step === 2 && (<>
                <H title="Where does your avatar live?" sub="Select one or more virtual worlds." />
                <div className="grid grid-cols-2 gap-3">
                  {WORLDS.map((w) => (
                    <button key={w} onClick={() => toggleWorld(w)} data-testid={`onb-world-${w.replace(" ", "")}`}
                      className={`p-4 rounded-xl border text-left transition-colors ${form.worlds.includes(w) ? "border-eblue bg-eblue/10" : "border-white/10 surface-2 hover:border-white/25"}`}>
                      <div className="flex items-center justify-between"><span className="font-display font-semibold">{w}</span>{form.worlds.includes(w) && <Check size={16} className="text-eblue" />}</div>
                    </button>
                  ))}
                </div>
              </>)}

              {step === 3 && (<>
                <H title="What's your industry?" sub="Choose a primary role (and an optional secondary)." />
                <div className="text-xs text-muted-foreground mb-2">Primary role</div>
                <div className="flex flex-wrap gap-2 mb-5">
                  {ROLES.map((r) => <Chip key={r} active={form.primary === r} onClick={() => set("primary", r)} label={r} testid={`onb-primary-${r}`} />)}
                </div>
                <div className="text-xs text-muted-foreground mb-2">Secondary role (optional)</div>
                <div className="flex flex-wrap gap-2">
                  {ROLES.filter((r) => r !== form.primary).map((r) => <Chip key={r} active={form.secondary === r} onClick={() => set("secondary", form.secondary === r ? null : r)} label={r} />)}
                </div>
              </>)}

              {step === 4 && (<>
                <H title="Connect external profiles" sub="Link the worlds where your avatar already lives (optional)." />
                <div className="space-y-3">
                  {form.links.map((l, i) => (
                    <div key={i} className="flex gap-2">
                      <select value={l.world} onChange={(e) => { const ls = [...form.links]; ls[i].world = e.target.value; set("links", ls); }} className="surface-2 border border-white/10 rounded-md px-2 text-sm w-32">
                        <option value="">World</option>{WORLDS.map((w) => <option key={w}>{w}</option>)}
                      </select>
                      <Input value={l.url} onChange={(e) => { const ls = [...form.links]; ls[i].url = e.target.value; set("links", ls); }} placeholder="Profile URL or username" className="h-10 bg-transparent border-white/10 flex-1" />
                    </div>
                  ))}
                  <button onClick={() => set("links", [...form.links, { world: "", url: "" }])} className="text-sm text-eblue">+ Add another</button>
                </div>
              </>)}

              {step === 5 && (<>
                <H title="Choose your membership" sub="You can upgrade anytime from your wallet." />
                <div className="grid sm:grid-cols-2 gap-3">
                  <button onClick={() => set("plan", "free")} className={`p-5 rounded-xl border text-left transition-colors ${form.plan === "free" ? "border-eblue bg-eblue/5" : "border-white/10 surface-2"}`} data-testid="onb-plan-free">
                    <div className="font-display font-bold text-lg">FREE</div><div className="text-2xl font-mono my-2">$0</div>
                    <ul className="text-xs text-muted-foreground space-y-1"><li>Profile, Feed, Stories, Reels</li><li>Earn VirtuCoins</li><li>Tipping & charts</li><li>100 VC withdrawal threshold</li></ul>
                  </button>
                  <button onClick={() => set("plan", "plus")} className={`p-5 rounded-xl border text-left transition-colors relative ${form.plan === "plus" ? "border-gold bg-gold/5" : "border-white/10 surface-2"}`} data-testid="onb-plan-plus">
                    <span className="absolute top-3 right-3"><Star size={16} className="text-gold fill-[var(--gold)]" /></span>
                    <div className="font-display font-bold text-lg text-gold-grad">VIRTUSTARS+</div><div className="text-2xl font-mono my-2">$14.99<span className="text-sm text-muted-foreground">/mo</span></div>
                    <ul className="text-xs text-muted-foreground space-y-1"><li>Music & TV uploads</li><li>Gold Star badge</li><li>50 VC withdrawal threshold</li><li>Advanced analytics & tools</li></ul>
                  </button>
                </div>
                {form.plan === "plus" && <p className="text-xs text-muted-foreground mt-3 text-center">You'll start free and can complete VirtuStars+ checkout from the upgrade page.</p>}
              </>)}

              <div className="flex gap-3 mt-8">
                {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} className="border-white/15 bg-white/5 rounded-xl" data-testid="onb-back"><ArrowLeft size={16} /></Button>}
                <Button onClick={next} disabled={busy} className="btn-blue flex-1 rounded-xl h-11" data-testid="onb-next">
                  {busy ? "Finishing…" : step === 5 ? "Enter the Industry" : "Continue"} {!busy && <ArrowRight size={16} className="ml-1" />}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 6 && (
          <div className="text-center animate-rise">
            <img src="/virtustars-logo.png" alt="" className="w-28 mx-auto mb-6" style={{ filter: "drop-shadow(0 0 40px rgba(212,175,55,0.5))" }} />
            <h1 className="font-display font-extrabold text-4xl text-gold-grad">WELCOME TO THE INDUSTRY</h1>
            <p className="text-silver mt-3">Your VStar career begins now, {form.display_name}.</p>
            <Loader2 className="animate-spin mx-auto mt-6 text-eblue" />
          </div>
        )}
      </div>
    </div>
  );
}

function H({ title, sub }) { return <div className="mb-6"><h2 className="font-display font-bold text-2xl">{title}</h2><p className="text-sm text-muted-foreground mt-1">{sub}</p></div>; }
function Chip({ active, onClick, label, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`px-3.5 py-2 rounded-full text-sm border transition-colors ${active ? "border-eblue bg-eblue/15 text-eblue" : "border-white/10 surface-2 text-muted-foreground hover:text-white hover:border-white/25"}`}>{label}</button>;
}
